<template>
  <div class="public-pass-view">
    <div class="d-flex align-start align-sm-center justify-center min-height-screen bg-grey-lighten-5">
      <VContainer class="pa-0 pa-sm-3">
        <VRow
          justify="center"
          class="ma-0 ma-sm-n3"
        >
          <VCol
            cols="12"
            sm="10"
            md="8"
            lg="6"
            xl="5"
            class="pa-0 pa-sm-3"
          >
            <!-- Loading State -->
            <VCard
              v-if="isFetchingPassData"
              class="pa-8 text-center"
            >
              <VProgressCircular 
                indeterminate 
                color="primary" 
                size="64"
                class="mb-4"
              />
              <p class="text-body-1">
                Loading visitor pass...
              </p>
            </VCard>

            <!-- Error State -->
            <VCard
              v-else-if="error"
              color="error"
              variant="outlined"
              class="pa-6 text-center"
            >
              <VIcon
                icon="tabler-alert-circle"
                size="64"
                class="mb-4 text-error"
              />
              <h3 class="text-h5 mb-3">
                Pass Not Found
              </h3>
              <p class="text-body-2 mb-4">
                {{ error }}
              </p>
              <VBtn 
                color="primary" 
                variant="outlined" 
                @click="$router.push('/')"
              >
                Go to Homepage
              </VBtn>
            </VCard>

            <!-- Visitor Pass Display -->
            <div v-else-if="passData">
              <!-- Header -->
              <!--
                <VCard
                class="mb-4 text-center"
                elevation="2"
                >
                <VCardText class="pa-6">
                <VIcon 
                icon="tabler-hospital" 
                size="48" 
                color="primary" 
                class="mb-3"
                />
                <h2 class="text-h4 font-weight-bold text-primary mb-2">
                Digital Visitor Pass
                </h2>
                <p class="text-body-1 text-medium-emphasis">
                Hospital Visitor Access Pass
                </p>
                </VCardText>
                </VCard> 
              -->

              <!-- Visitor Pass Card (Reusing same structure as success page) -->
              <VCard
                id="public-visitor-pass-card"
                class="visitor-pass mx-auto mb-4"
                variant="flat"
              >
                <!-- Pass Header -->
                <VCardTitle class="pa-4 text-white pass-header">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <VIcon 
                        icon="tabler-hospital" 
                        size="32" 
                        class="me-3"
                      />
                      <div class="text-start">
                        <h5 class="text-h6 font-weight-bold text-white">
                          {{ passData.unit?.display_name ?? passData.unit?.name }}
                        </h5>
                        <p class="text-caption mb-0 pass-subtitle">
                          For check-in & check-out during visits
                        </p>
                      </div>
                    </div>
                    <!--
                      <VChip
                      :color="passData.status === 'active' ? 'success' : 'warning'"
                      variant="elevated"
                      size="small"
                      >
                      {{ passData.status === 'active' ? 'Active' : 'Expired' }}
                      </VChip> 
                    -->
                  </div>
                </VCardTitle>

                <!-- Pass Content -->
                <VCardText class="pa-6">
                  <!-- Multiple Visitors Info -->
                  <!--
                    <div v-if="qrCodes.length > 1" class="text-center mb-4">
                    <VChip
                    color="info"
                    variant="elevated"
                    size="small"
                    >
                    {{ qrCodes.length }} Visitors
                    </VChip>
                    </div> 
                  -->

                  <!-- QR Code Carousel -->
                  <div class="text-center mb-6">
                    <VCarousel
                      v-model="currentCarouselIndex"
                      :show-arrows="passData.visitors.length > 1"
                      hide-delimiters
                      height="auto"
                      class="qr-carousel"
                    >
                      <!-- Custom Previous Arrow -->
                      <template #prev="{ props }">
                        <VBtn
                          v-bind="props"
                          variant="tonal"
                          color="primary"
                          icon
                          size="small"
                          class="rounded carousel-arrow-btn"
                        >
                          <VIcon
                            size="28"
                            icon="tabler-chevron-left"
                          />
                        </VBtn>
                      </template>

                      <!-- Custom Next Arrow -->
                      <template #next="{ props }">
                        <VBtn
                          v-bind="props"
                          variant="tonal"
                          color="primary"
                          icon
                          size="small"
                          class="rounded carousel-arrow-btn"
                        >
                          <VIcon
                            size="28"
                            icon="tabler-chevron-right"
                          />
                        </VBtn>
                      </template>
                      <VCarouselItem
                        v-for="(visitor, index) in passData.visitors"
                        :key="index"
                      >
                        <div class="visitor-card-content">
                          <!-- Visitor Info -->
                          <div class="d-flex align-center mb-4">
                            <VAvatar
                              color="primary"
                              size="48"
                              class="me-3 rounded"
                            >
                              <VIcon
                                :icon="`tabler-number-${index + 1}`"
                                size="24"
                              />
                            </VAvatar>

                            <div class="text-start flex-grow-1">
                              <h6 class="text-h6 font-weight-bold mb-1">
                                {{ visitor.visitor_name }}
                              </h6>
                              <p class="text-caption text-medium-emphasis mb-0">
                                Pass #{{ visitor.pass_code }}
                              </p>
                              <p class="text-caption text-medium-emphasis mb-0">
                                Last Visit: {{ visitor.last_visit_at }}
                              </p>
                            </div>

                            <VSpacer />

                            <VChip
                              v-if="visitor.is_expired"
                              color="error"
                              variant="elevated"
                            >
                              Expired
                            </VChip>
                            <VChip
                              v-else-if="visitor.is_session_active"
                              color="warning"
                              variant="elevated"
                            >
                              Ongoing
                            </VChip> 
                            <VChip
                              v-else-if="visitor.is_active"
                              color="success"
                              variant="elevated"
                            >
                              Active
                            </VChip>
                            <VChip
                              v-else
                              color="success"
                              variant="elevated"
                              class="text-capitalize"
                            >
                              {{ visitor.status }}
                            </VChip> 
                          </div>

                          <!-- {{ visitor }} -->

                          <!-- QR Code -->
                          <div class="qr-code-container">
                            <div class="qr-code-wrapper">
                              <VueQrcode
                                :value="visitor.qr_code"
                                :options="{
                                  width: 200,
                                  color: {
                                    dark: '#1976d2',
                                    light: '#ffffff'
                                  },
                                  margin: 2
                                }"
                                tag="canvas"
                                class="qr-code-image"
                                :class="{ 'qr-code-blurred': visitor.is_reached_max_visits || visitor.is_reached_max_visits_per_day }"
                              />
                              <VChip
                                v-if="visitor.is_reached_max_visits || visitor.is_reached_max_visits_per_day"
                                color="error"
                                variant="elevated"
                                class="qr-overlay-chip"
                              >
                                Maximum Limit Reached
                              </VChip>
                            </div>
                            <p class="text-caption text-medium-emphasis mt-3 mb-4">
                              Scan this QR code at entry/exit points
                            </p>

                            <!-- Individual Download/Share Buttons -->
                            <VRow dense>
                              <VCol cols="6">
                                <VBtn
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                  block
                                  prepend-icon="tabler-download"
                                  @click="downloadQrCode(index)"
                                >
                                  Download
                                </VBtn>
                              </VCol>
                              <VCol cols="6">
                                <VBtn
                                  color="success"
                                  variant="outlined"
                                  size="small"
                                  block
                                  prepend-icon="tabler-share"
                                  @click="shareQrCode(index)"
                                >
                                  Share
                                </VBtn>
                              </VCol>
                            </VRow>
                          </div>
                          
                          <!--
                            <VAlert
                            v-if="visitor.is_session_active"
                            class="mt-4 text-left"
                            border="start"
                            color="error"
                            variant="tonal"
                            >
                            This pass is already in use.
                            </VAlert> 
                          -->

                          <!-- Patient Details for this visitor -->
                          <!--
                            <VDivider class="my-4" />
                            <div class="patient-details-mini">
                            <p class="text-caption text-medium-emphasis mb-2 font-weight-bold">
                            Visiting:
                            </p>
                            <VRow dense>
                            <VCol cols="6">
                            <div class="text-caption text-medium-emphasis">Patient</div>
                            <div class="text-body-2 font-weight-medium">{{ visitor.patient.name }}</div>
                            </VCol>
                            <VCol cols="6">
                            <div class="text-caption text-medium-emphasis">Department</div>
                            <div class="text-body-2 font-weight-medium">{{ visitor.patient.department }}</div>
                            </VCol>
                            </VRow>
                            </div> 
                          -->
                        </div>
                      </VCarouselItem>
                    </VCarousel>

                    <VCard
                      v-if="currentVisitor"
                      class="mt-4 border"
                      variant="flat"
                    >
                      <VCardText class="pa-4 text-left">
                        <div>
                          <strong>Pass Type:</strong> {{ currentVisitor.is_bystander_24hrs ? 'Bystander' : 'Normal' }}
                        </div>
                        <div class="d-flex flex-wrap align-center gap-1">
                          <!--
                            Always shown. A bystander pass and an unrestricted
                            pass both carry no windows, so keying the row off
                            visiting_windows.length hid it entirely — while the
                            WhatsApp message for the same pass still said
                            "24Hrs". State what applies instead of vanishing.
                          -->
                          <strong>
                            Visiting Time:
                          </strong>
                          <div class="d-flex flex-wrap gap-2">
                            <VChip
                              v-if="currentVisitor.is_bystander_24hrs"
                              color="success"
                              variant="tonal"
                              size="small"
                              class="px-2"
                            >
                              <span>24 Hrs</span>
                            </VChip>
                            <VChip
                              v-else-if="!currentVisitor.visiting_windows?.length"
                              color="secondary"
                              variant="tonal"
                              size="small"
                              class="px-2"
                            >
                              <span>No restriction</span>
                            </VChip>
                            <template v-else>
                              <VChip
                                v-for="(window, index) in currentVisitor.visiting_windows"
                                :key="index"
                                color="primary"
                                variant="tonal"
                                size="small"
                                class="px-2"
                              >
                                <span>{{ window.start }} - {{ window.end }}</span>
                              </VChip>
                            </template>
                          </div>
                        </div>
                        <div v-if="currentVisitor.max_visits">
                          <strong>Max Visits Used:</strong> {{ currentVisitor.max_visits_used }} / {{ currentVisitor.max_visits }}
                        </div>
                        <div v-if="currentVisitor.max_visits_per_day">
                          <strong>Max Visits Per Day Used:</strong> {{ currentVisitor.max_visits_per_day_used }} / {{ currentVisitor.max_visits_per_day }}
                        </div>
                        <div>
                          <strong>Created By:</strong> {{ currentVisitor?.created_by_user?.name || 'System' }}
                        </div>
                        <div>
                          <strong>Created At:</strong> {{ currentVisitor?.created_at ? moment(currentVisitor.created_at).format('YYYY-MM-DD HH:mm:ss') : 'N/A' }}
                        </div>
                      </VCardText>
                    </VCard>

                    <VAlert
                      v-if="currentVisitor?.remark"
                      class="mt-4 text-left"
                      border="start"
                      color="primary"
                      variant="tonal"
                    >
                      <strong>Remark:</strong> {{ currentVisitor?.remark }}
                    </VAlert>

                    <VAlert
                      v-if="currentVisitor?.is_session_active"
                      class="mt-4 text-left"
                      border="start"
                      color="error"
                      variant="tonal"
                    >
                      This pass is already in use.
                    </VAlert>
                    <VAlert
                      v-else-if="currentVisitor?.is_reached_max_visits_per_day"
                      class="mt-4 text-left"
                      border="start"
                      color="error"
                      variant="tonal"
                    >
                      This visitor has reached the maximum number of visits allowed for today.
                    </VAlert>
                    <VAlert
                      v-else-if="currentVisitor?.is_reached_max_visits"
                      class="mt-4 text-left"
                      border="start"
                      color="error"
                      variant="tonal"
                    >
                      This visitor has reached the maximum number of visits allowed.
                    </VAlert>

                    <!-- Carousel Indicators with Visitor Names -->
                    <!--
                      <div v-if="qrCodes.length > 1" class="qr-indicators mt-4">
                      <VChip
                      v-for="(visitor, index) in qrCodes"
                      :key="index"
                      :color="currentCarouselIndex === index ? 'primary' : 'default'"
                      size="small"
                      class="mx-1 mb-1"
                      @click="currentCarouselIndex = index"
                      >
                      <VIcon
                      icon="tabler-user"
                      size="14"
                      class="me-1"
                      />
                      {{ visitor.visitor_name.split(' ')[0] }}
                      </VChip>
                      </div> 
                    -->
                  </div>

                  <div v-if="passData.visitors?.length === 0">
                    <VAlert color="error">
                      <strong>No Valid Pass Available</strong>
                      <div>Please contact with administration for assistance.</div>
                    </VAlert>
                  </div>
                  
                  <VDivider class="mb-4" />

                  <!-- Patient Details -->
                  <div
                    v-if="passData?.patient"
                    class="patient-details-full"
                  >
                    <h6 class="text-subtitle-2 font-weight-bold mb-3 text-primary">
                      Patient Information
                    </h6>
                    <VRow dense>
                      <VCol cols="6">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            Patient Name
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.patient.name || 'N/A' }}
                          </div>
                        </div>
                      </VCol>

                      <VCol cols="6">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            UHID
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.patient.uhid || 'N/A' }}
                          </div>
                        </div>
                      </VCol>

                      <!--
                        <VCol cols="6">
                        <div class="detail-item">
                        <div class="text-caption text-medium-emphasis">
                        Department
                        </div>
                        <div class="text-body-2 font-weight-bold">
                        {{ passData.patient.department || 'N/A' }}
                        </div>
                        </div>
                        </VCol> 
                      -->

                      <VCol cols="12">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            Location
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.patient.location || 'N/A' }}
                          </div>
                        </div>
                      </VCol>
                    </VRow>
                  </div>

                  <!-- Validity Information -->
                  <!--
                    <VAlert 
                    :color="passData.status === 'active' ? 'info' : 'warning'" 
                    variant="tonal" 
                    class="mt-4 mb-0"
                    density="compact"
                    >
                    <div class="d-flex align-center">
                    <VIcon
                    :icon="passData.status === 'active' ? 'tabler-clock' : 'tabler-alert-triangle'"
                    class="me-2"
                    />
                    <div>
                    <div class="text-body-2 font-weight-medium">
                    {{ passData.status === 'active' ? 'Valid until:' : 'Expired on:' }} {{ passData.expires_at ? formatDateTime(passData.expires_at) : 'N/A' }}
                    </div>
                    <div class="text-caption">
                    {{ passData.status === 'active' ? 'Present this pass with valid ID' : 'This pass is no longer valid' }}
                    </div>
                    </div>
                    </div>
                    </VAlert> 
                  -->
                </VCardText>
              </VCard>

              <!--
                Guideline authored per visiting rule and read live off that
                rule, so an edit reaches passes that already exist.
              -->
              <VCard
                v-if="passData?.guideline"
                variant="flat"
                class="guideline-card mx-auto border mb-4 text-start"
              >
                <VCardText class="pa-4">
                  <h6 class="text-subtitle-2 font-weight-bold mb-3 text-primary">
                    Guideline
                  </h6>
                  <div class="text-body-2 guideline-content">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-html="passData.guideline" />
                  </div>
                </VCardText>
              </VCard>

              <!-- Instructions -->
              <!--
                <VAlert 
                color="info" 
                variant="outlined"
                class="text-start mb-4"
                prominent
                >
                <template #prepend>
                <VIcon icon="tabler-info-circle" />
                </template>
                
                <h6 class="text-subtitle-1 font-weight-bold mb-3">
                Instructions:
                </h6>
                <ol class="text-body-2">
                <li>Present this digital pass at the hospital reception</li>
                <li>Bring valid photo identification with you</li>
                <li>Follow all hospital visitor guidelines and policies</li>
                <li>Respect visiting hours and patient privacy</li>
                </ol>
                </VAlert> 
              -->
            </div>
          </VCol>
        </VRow>
      </VContainer>
    </div>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import moment from 'moment'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const AUTO_REFRESH_INTERVAL = 60000 // 60 seconds

// Composables
const route = useRoute()
const router = useRouter()
const snackbar = useToast()

// Define page metadata
definePage({
  meta: {
    layout: 'blank',
    public: true,
    unauthenticatedOnly: true,
    title: 'Visitor Pass',
  },
})

// const episode = computed(() => route.params.episode)

const episodeId = computed(() => route.query.e)
const visitorQRCodeId = computed(() => route.query.q)

if (!episodeId.value) {
  router.push({
    name: 'invalid-url',
  })
}

// Reactive data
// const loading = ref(true)
const error = ref(null)
const passData = ref(null)
const currentCarouselIndex = ref(0)

// Computed QR Codes
// const qrCodes = computed(() => {
//   if (!passData.value || !passData.value.visitors) return []

//   // Generate QR codes for each visitor
//   return passData.value.visitors.map(visitor => ({
//     visitor_name: visitor.visitor_name,
//     pass_code: visitor.pass_code,
//     created_at: visitor.created_at,
//     patient: passData.value.patient,
//     value: visitor.qr_code,
//   }))
// })

const visitorPassApi = computed(() => {
  let link = `/care-visit/public/visitors/pass/${episodeId.value}`

  if (visitorQRCodeId.value) {
    link = `${link}/${visitorQRCodeId.value}`
  }

  return link
})

const {
  isFetching: isFetchingPassData,
  execute: fetchPassData,
} = await useApi(createUrl(visitorPassApi.value), {
  beforeFetch: ({ cancel }) => {
    if (!episodeId.value) cancel()
  },
  afterFetch: ({ data }) => {
    passData.value = {
      unit: data.episode?.unit,
      guideline: data.guideline ?? null,
      status: 'active',
      created_at: '2024-11-15T10:00:00Z',
      expires_at: '2024-11-15T22:00:00Z',
      patient: {
        name: data.episode?.customer?.name ?? 'N/A',
        uhid: data.episode?.customer?.uhid ?? 'N/A',
        location: data.episode?.location?.name ?? 'N/A',
      },
      visitors: data.visitor_qr_codes.map(item => {
        let isSessionActive = false
        if (
          item.last_visitor_session?.checked_in_at &&
          !item.last_visitor_session?.checked_out_at
        ) {
          isSessionActive = true
        }

        let isReachedMaxVisits = false
        if (
          item.max_visits !== null && 
          item.max_visits_used >= item.max_visits
        ) {
          isReachedMaxVisits = true
        }

        let isReachedMaxVisitsPerDay = false
        if (
          item.max_visits_per_day !== null && 
          item.max_visits_per_day_used >= item.max_visits_per_day
        ) {
          isReachedMaxVisitsPerDay = true
        }

        return {
          visitor_name: item.visitor.name,
          pass_code: item.code,
          last_visit_at: item.last_visitor_session?.checked_in_at ? moment(item.last_visitor_session.checked_in_at).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
          qr_code: item.qr_token,
          is_session_active: isSessionActive,
          is_expired: item.is_expired,
          is_active: item.is_active,
          status: item.status,
          max_visits: item.max_visits,
          max_visits_used: item.max_visits_used,
          max_visits_per_day: item.max_visits_per_day,
          max_visits_per_day_used: item.max_visits_per_day_used,
          is_reached_max_visits: isReachedMaxVisits,
          is_reached_max_visits_per_day: isReachedMaxVisitsPerDay,
          is_bystander_24hrs: item.is_bystander_24hrs,
          visiting_windows: item.visiting_windows ?? [],
          created_by_user: item.created_by_user,
          created_at: item.created_at,
          remark: item.remark,
        }
      }),
    }
  },
  onError: error => {
    router.push({
      name: 'invalid-url',
    })
  },
})

const currentVisitor = computed(() => {
  if (passData.value?.visitors) {
    return passData.value?.visitors[currentCarouselIndex.value]
  }

  return null
})

const downloadQrCode = index => {
  try {
    const visitor = passData.value.visitors[index]
    const canvas = document.querySelectorAll('.qr-code-image')[index]

    if (!canvas) {
      snackbar.error('QR code not found')

      return
    }

    // Convert canvas to blob and download
    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.download = `${visitor.visitor_name.replace(/\s+/g, '_')}_${visitor.pass_code}.png`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
      snackbar.success(`QR code for ${visitor.visitor_name} downloaded successfully!`)
    })
  } catch (err) {
    console.error('Error downloading QR code:', err)
    snackbar.error('Failed to download QR code')
  }
}

const shareQrCode = async index => {
  try {
    const visitor = passData.value.visitors[index]
    const canvas = document.querySelectorAll('.qr-code-image')[index]

    if (!canvas) {
      snackbar.error('QR code not found')
      
      return
    }

    // Convert canvas to blob for sharing
    canvas.toBlob(async blob => {
      const file = new File([blob], `${visitor.visitor_name}_QR.png`, { type: 'image/png' })

      const shareData = {
        title: `Hospital Visitor Pass - ${visitor.visitor_name}`,
        text: `Hospital Visitor Pass\n👤 ${visitor.visitor_name}\n🆔 ${visitor.pass_code}\n🏥 Visiting: ${passData.value.patient.name}`,
        files: [file],
      }

      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData)
        snackbar.success(`QR code for ${visitor.visitor_name} shared successfully!`)
      } else if (navigator.share) {
        // Fallback without file
        await navigator.share({
          title: shareData.title,
          text: `${shareData.text}\n\nView the full pass: ${window.location.href}`,
          url: window.location.href,
        })
        snackbar.success(`QR code for ${visitor.visitor_name} shared successfully!`)
      } else {
        // Fallback: copy URL to clipboard
        await navigator.clipboard.writeText(window.location.href)
        snackbar.success('Pass link copied to clipboard!')
      }
    })
  } catch (err) {
    console.error('Error sharing QR code:', err)
    snackbar.error('Failed to share QR code')
  }
}

// Auto-refresh interval reference
let refreshInterval = null

// Handle visibility change (when user returns to the page)
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    fetchPassData()
  }
}

// Lifecycle
onMounted(() => {
  // Set up auto-refresh every 30 seconds
  refreshInterval = setInterval(() => {
    fetchPassData()
  }, AUTO_REFRESH_INTERVAL)

  // Listen for visibility changes (when user returns from background)
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  // Clean up interval
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }

  // Remove visibility change listener
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped>
.min-height-screen {
  min-block-size: 100vh;
}

/* Reuse visitor pass styles */
.visitor-pass {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  max-inline-size: 420px;
}

/* Mobile: Edge-to-edge, no border radius, no border, full height */
@media (max-width: 599.98px) {
  .visitor-pass {
    border: none;
    border-radius: 0;
    max-inline-size: 100%;
    min-block-size: 100vh;
    margin-inline: 0 !important;
    margin-block-end: 0 !important;
  }

  .visitor-pass .pass-header {
    border-radius: 0;
  }
}

.pass-header {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
}

.pass-header::before {
  position: absolute;
  animation: shine 3s ease-in-out infinite;
  background: radial-gradient(circle, rgba(255,255,255,10%) 0%, transparent 70%);
  block-size: 200%;
  content: '';
  inline-size: 200%;
  inset-block-start: -50%;
  inset-inline-end: -50%;
}

.pass-subtitle {
  opacity: 0.9;
}

.qr-carousel {
  background: transparent !important;
}

/* .carousel-arrow-btn {
  border-radius: 50% !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
} */

.visitor-card-content {
  padding-block: 0;
  padding-inline: 0;
}

.qr-code-container {
  padding: 16px;
  border-radius: 12px;
  background: white;
  box-shadow: inset 0 2px 4px rgba(0,0,0,10%);
}

.qr-code-wrapper {
  position: relative;
  display: inline-block;
}

.qr-code-image {
  border-radius: 8px;
  transition: filter 0.3s ease;
}

.qr-code-blurred {
  filter: blur(4px);
}

.qr-overlay-chip {
  position: absolute;
  z-index: 10;
  font-weight: 600;
  inset-block-start: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
}

.patient-details-mini {
  padding: 12px;
  border-radius: 8px;
  background: rgba(25, 118, 210, 5%);
}

.patient-details-full {
  padding-block: 8px;
  padding-inline: 0;
}

.qr-indicators {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.qr-indicators .v-chip {
  cursor: pointer;
  transition: all 0.3s ease;
}

.qr-indicators .v-chip:hover {
  transform: scale(1.05);
}

.detail-item {
  padding-block: 8px;
  padding-inline: 0;
}

.detail-item:not(:last-child) {
  border-block-end: 1px solid rgba(0,0,0,5%);
}

@keyframes shine {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Print styles */
@media print {
  .min-height-screen {
    min-block-size: auto;
  }
  
  #public-visitor-pass-card {
    border: 2px solid #1976d2 !important;
    box-shadow: none !important;
    max-inline-size: none !important;
  }
  
  .pass-header {
    background: #1976d2 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .v-chip {
    background: #4caf50 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .v-avatar {
    background: #1976d2 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .v-alert {
    border-color: #1976d2 !important;
    background: #e3f2fd !important;
    color: #1976d2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* Track .visitor-pass so the guideline lines up with the pass above it
   instead of spilling to the full column width. */
.guideline-card {
  max-inline-size: 420px;
}

@media (max-width: 599.98px) {
  .guideline-card {
    border-inline: none;
    border-radius: 0;
    max-inline-size: 100%;
    margin-inline: 0 !important;
  }
}

/* Tiptap markup arrives through v-html, which scoped rules never reach. */
.guideline-content :deep(p) {
  margin-block-end: 0.5rem;
}

.guideline-content :deep(p:last-child) {
  margin-block-end: 0;
}

.guideline-content :deep(ul),
.guideline-content :deep(ol) {
  margin-block-end: 0.5rem;
  padding-inline-start: 1.25rem;
}
</style>
